import React, { useCallback } from 'react'
import classNames from 'classnames'
import { Case, Switch } from 'react-if'
import { GuildScore } from '@/atoms/GuildScore'
import { Image } from '@/atoms/Image'
import { CaptionLG, CaptionMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { Project } from '@/services/ProjectsService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export interface CollectionContentCardProps {
  relatedProject?: Project
  className?: string
}

export const CollectionContentCard: React.FC<CollectionContentCardProps> = ({ relatedProject, className }) => {
  const { t } = useTranslate('watch')
  const track = useSafeTrack()

  const onClick = useCallback(() => {
    track('Watch Project Franchise Project Clicked', { project: relatedProject?.slug })
  }, [relatedProject?.slug, track])

  const portraitProjectPoster =
    relatedProject?.title?.portraitAngelImage?.cloudinaryPath ??
    relatedProject?.title?.portraitAngelImage3?.cloudinaryPath ??
    relatedProject?.title?.portraitTitleImage?.cloudinaryPath ??
    ''

  return (
    <div
      aria-label="suggested-content-card"
      id={relatedProject?.id ?? ''}
      className="relative min-w-[250px] pr-6 lg:min-w-[316px]"
    >
      <div className={classNames('flex flex-col', className)}>
        {portraitProjectPoster && (
          <a
            aria-label={relatedProject?.name ?? ''}
            href={`${paths.watch.index}/${relatedProject?.slug}`}
            className="aspect-h-3 aspect-w-2 relative mb-3 w-full cursor-pointer overflow-hidden rounded-2xl"
            onClick={onClick}
          >
            <div className="absolute inset-0">
              <Image
                alt={relatedProject?.title?.name ?? ''}
                src={getCloudinaryImageUrl({ path: portraitProjectPoster })}
                width={316}
                height={474}
                className="h-full w-full object-cover"
              />
            </div>
          </a>
        )}
        <div className="flex flex-col gap-y-1">
          <CaptionMD color="core-gray-500">
            <Switch>
              <Case condition={relatedProject?.title?.__typename === 'ContentSeries'}>
                <Translate i18nKey="series" t={t}>
                  Series
                </Translate>
              </Case>
              <Case condition={relatedProject?.title?.__typename === 'ContentMovie'}>
                <Translate i18nKey="movie" t={t}>
                  Movie
                </Translate>
              </Case>
              <Case condition={relatedProject?.title?.__typename === 'ContentSpecial'}>
                <Translate i18nKey="special" t={t}>
                  Special
                </Translate>
              </Case>
            </Switch>
          </CaptionMD>
          <CaptionLG weight="bold">{relatedProject?.name}</CaptionLG>
          {relatedProject?.highestScore ? <GuildScore guildScore={relatedProject?.highestScore} /> : null}
        </div>
      </div>
    </div>
  )
}
